import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Form, Input, InputNumber, Modal,
  Popconfirm, Radio, Row, Select, Space, Spin, Statistic, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { spaces, video } from '../../../services/api';
import { getStoredUser } from '../../../utils/auth';
import EzvizPlayer from '../../../components/EzvizPlayer';

export default function VideoMonitoring() {
  const isAdmin = getStoredUser().role === 'admin';
  const [loading, setLoading] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [spaceList, setSpaceList] = useState([]);
  const [status, setStatus] = useState(null);
  const [spaceFilter, setSpaceFilter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, cameraRes, spaceRes] = await Promise.all([
        video.status(), video.list(), spaces.list(),
      ]);
      const rows = cameraRes.data || [];
      setStatus(statusRes.data);
      setCameras(rows);
      setSpaceList(spaceRes.data || []);
      setSelected(current => current ? rows.find(item => item.id === current.id) || null : null);
    } catch (err) {
      message.error(err.response?.data?.message || err.message || '加载视频监控失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ channel_no: 1, stream_type: 'sub' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const saveCamera = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        await video.update(editing.id, values);
        message.success('摄像头档案已更新');
      } else {
        await video.create(values);
        message.success('摄像头档案已创建，首次播放时将初始化设备级授权');
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const removeCamera = async (record) => {
    try {
      await video.remove(record.id);
      message.success('摄像头已停用');
      if (selected?.id === record.id) setSelected(null);
      await loadData();
    } catch (err) {
      message.error(err.response?.data?.message || err.message || '停用失败');
    }
  };

  const filteredCameras = spaceFilter
    ? cameras.filter(camera => camera.space_id === spaceFilter)
    : cameras;

  const columns = [
    { title: '摄像头', dataIndex: 'name', render: (value, record) => <Button type="link" style={{ padding: 0 }} onClick={() => setSelected(record)}>{value}</Button> },
    { title: '所属站点', dataIndex: 'station_name', render: value => value || '-' },
    { title: '安装位置', dataIndex: 'location', render: value => value || '-' },
    { title: '通道', dataIndex: 'channel_no', width: 70 },
    { title: '授权状态', dataIndex: 'authorization_status', width: 100, render: value => <Tag color={value === '已授权' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '操作', width: 190, render: (_, record) => (
        <Space size={4}>
          <Button size="small" type="primary" ghost onClick={() => setSelected(record)}>播放</Button>
          {isAdmin && <Button size="small" onClick={() => openEdit(record)}>编辑</Button>}
          {isAdmin && (
            <Popconfirm title="确认停用该摄像头？" onConfirm={() => removeCamera(record)}>
              <Button size="small" danger>停用</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (loading && cameras.length === 0) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="已接入摄像头" value={cameras.length} suffix="路" prefix={<VideoCameraOutlined />} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="覆盖站点" value={new Set(cameras.map(camera => camera.space_id)).size} suffix="个" /></Card></Col>
        <Col xs={24} md={12}>
          <Card size="small" style={{ height: '100%' }}>
            <Space direction="vertical" size={4}>
              <Typography.Text strong>萤石开放平台</Typography.Text>
              <Space><Typography.Text type="secondary">接入状态</Typography.Text><Tag color={status?.configured ? 'green' : 'orange'}>{status?.configured ? '已配置' : '待配置密钥'}</Tag></Space>
            </Space>
          </Card>
        </Col>
      </Row>

      {!status?.configured && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="真实视频尚未启用"
          description="可以先维护摄像头档案。由管理员在服务端配置 EZVIZ_APP_KEY 和 EZVIZ_APP_SECRET 后，首次播放会自动初始化设备级子账户授权。"
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card
            size="small" title="摄像头档案"
            extra={(
              <Space>
                <Select
                  allowClear placeholder="全部站点" style={{ width: 180 }} value={spaceFilter}
                  onChange={setSpaceFilter}
                  options={spaceList.map(space => ({ value: space.id, label: space.name }))}
                />
                <Button icon={<ReloadOutlined />} onClick={loadData} />
                {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增</Button>}
              </Space>
            )}
          >
            <Table
              rowKey="id" size="small" columns={columns} dataSource={filteredCameras}
              pagination={{ pageSize: 8, hideOnSinglePage: true }} scroll={{ x: 760 }}
              locale={{ emptyText: '暂无摄像头档案，请先由管理员新增' }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card size="small" title={selected ? `实时画面：${selected.name}` : '实时画面'}>
            {selected ? (
              <>
                <EzvizPlayer key={selected.id} camera={selected} />
                <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
                  {selected.station_name || '未关联站点'} · 通道 {selected.channel_no} · {selected.stream_type === 'main' ? '主码流' : '子码流'}。切换摄像头或离开页面时自动关闭取流。
                </Typography.Paragraph>
              </>
            ) : <Empty description="从左侧选择一路摄像头开始播放" style={{ padding: '100px 0' }} />}
          </Card>
        </Col>
      </Row>

      <Modal
        title={editing ? '编辑摄像头' : '新增摄像头'} open={modalOpen}
        onOk={saveCamera} confirmLoading={saving}
        onCancel={() => setModalOpen(false)} destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="space_id" label="所属站点" rules={[{ required: true, message: '请选择所属站点' }]}>
            <Select showSearch optionFilterProp="label" options={spaceList.map(space => ({ value: space.id, label: space.name }))} />
          </Form.Item>
          <Form.Item name="name" label="摄像头名称" rules={[{ required: true, message: '请输入摄像头名称' }]}>
            <Input placeholder="例如：充电区全景" maxLength={200} />
          </Form.Item>
          <Form.Item name="device_serial" label="设备序列号" rules={[{ required: true, message: '请输入设备序列号' }, { pattern: /^[A-Za-z0-9-]{4,50}$/, message: '仅支持字母、数字和短横线' }]}>
            <Input placeholder="萤石开放平台中的设备序列号" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="channel_no" label="通道号" rules={[{ required: true, message: '请输入通道号' }]}>
                <InputNumber min={1} max={64} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="stream_type" label="默认码流" rules={[{ required: true }]}>
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio.Button value="sub">子码流</Radio.Button>
                  <Radio.Button value="main">主码流</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="location" label="安装位置">
            <Input placeholder="例如：站区北侧立杆" maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
